import useFocusTrap from '@charlietango/use-focus-trap';
import React, { FC, ReactNode, useCallback, useLayoutEffect } from 'react';
import styled, { css } from 'styled-components';

import { MEDIA_QUERIES, useNavigation } from '#/hooks';
import { noop } from '#/service/utils';

import { Logo, LogoContainer } from '../Logo';
import { ReactPortal } from '../Portal';
import {
  BackContainer,
  BackImage,
  ModalBody,
  StyledOverlay,
} from './FullPageModal.styled';

interface Props {
  children?: ReactNode;
  /**
   * @optional Background image URL.
   * If not provided, the default (`var(--pageBackground)`) background color will be used
   * */
  backgroundImageUrl?: string;

  /** @default false Shows a standard logo component on the overlay (displayed in top left corner) */
  showLogo?: boolean;

  /** @default false If `true`, the modal will close when the `Esc` key is pressed */
  closeOnEsc?: boolean;

  /**
   * @default true Applies a statically defined linear gradient on the background image
   * Disable this if you want to use background image with a gradient already applied
   * */
  applyDefaultGradient?: boolean;

  /** @optional Handler that will run whenever modal closes */
  onClose?: () => void;
}

interface FullPageModalComponent extends FC<Props> {
  /** A convenient to use Full Page Modal Title heading component */
  Title: FC<Props>;

  /** A convenient to use Full Page Modal Subtitle message component */
  Subtitle: FC<Props>;

  /**
   * A convenient to use content container that is aligned to the center of the page.
   * It will be centered on big screens and aligned to the top on tablets and mobiles
   * */
  CenteredContent: FC<CenteredContentProps | Props>;
}

/**
 * This is a full page modal. You can use it when you have a design that hides all other parts of the application (i.e. menu, footer, etc)
 * It's not a perfect solution but for now it works fine. At some point we should replace it with something more convenient and accessibility-friendly
 * This is a compount component. You can use it like this:
 * @example
 * <FullPageModal backgroundImageUrl={backgroundImageUrl} showLogo>
     <FullPageModal.CenteredContent>
       <FullPageModal.Title>Lovely title</FullPageModal.Title>
       ...
     </FullPageModal.CenteredContent>
    </FullPageModal>
 */
export const FullPageModal: FullPageModalComponent = ({
  children,
  backgroundImageUrl,
  showLogo,
  applyDefaultGradient = true,
  onClose = noop,
  closeOnEsc = false,
  ...rest
}) => {
  // by default, useFocusTrap would automatically focus on the first focusable element,
  // which would also scroll it into view. the only way to prevent this is to pass a
  // selector that won't find anything. the user will still not be able to focus outside.
  const ref = useFocusTrap(true, { focusSelector: '#non-existent-id' });

  const { goHome } = useNavigation('FullPageModal');

  useLayoutEffect(() => {
    // We have to actually remove the body scrollbar here for aesthetic reasons
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  const onKeyDown = useCallback(
    (ev: React.KeyboardEvent) => {
      if (ev.key === 'Escape' && closeOnEsc && onClose) {
        ev.stopPropagation();
        onClose();
      }
    },
    [closeOnEsc, onClose]
  );

  return (
    <ReactPortal>
      <StyledOverlay
        data-nosnippet
        data-testid="fullPageModal-backdrop"
        onKeyDown={onKeyDown}
        ref={ref}
      >
        {backgroundImageUrl && (
          <BackContainer>
            <BackImage
              data-apply-gradient={applyDefaultGradient}
              src={backgroundImageUrl}
            />
          </BackContainer>
        )}
        <ModalBody data-testid="fullPageModal-body" role="dialog" {...rest}>
          {children}
        </ModalBody>
        {showLogo && (
          <LogoContainer>
            <Logo onClick={goHome} />
          </LogoContainer>
        )}
      </StyledOverlay>
    </ReactPortal>
  );
};

FullPageModal.Title = styled.h1`
  text-align: center;
  font: var(--fontStyleHeadline);
  max-width: 50rem;
  color: var(--pageHeadingForeground);

  @media ${MEDIA_QUERIES.xs} {
    font: var(--fontStyleTitle2);
  }
`;

FullPageModal.Subtitle = styled.p`
  text-align: center;
  font: var(--fontStyleSubtitle);
  font-weight: 325;
  font-size: 1.1rem;
  line-height: 1.5rem;
  max-width: 38rem;
`;

interface CenteredContentProps {
  /**
   * @optional Gap between children. Please note that providing value here
   * will disable margins on direct children
   */
  $gap?: string | 0;
}

FullPageModal.CenteredContent = styled.div<CenteredContentProps>`
  align-self: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1rem;

  ${({ $gap }) =>
    $gap &&
    css`
      gap: ${$gap};

      > * {
        margin: 0;
      }
    `}
`;
